import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

import ReactEChartsCore from 'echarts-for-react/lib/core';
import { LineChart, BarChart } from 'echarts/charts';
import * as echarts from 'echarts/core';
import {
  GridComponent,
  TooltipComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

import { getColor, rgbaColor, numberFormatter } from '../../helpers/utils';
import LicenseService from '../licenses/services';

echarts.use([
  TooltipComponent,
  GridComponent,
  LineChart,
  BarChart,
  CanvasRenderer,
]);

/**
 * Line or bar chart of outgoing email usage.
 * months: 4 or 12. chartType: 'line' | 'bar'.
 */
const EmailUsageMonthlyChart = ({ months = 4, chartType = 'line' }) => {
  const [data, setData] = useState([]);
  const [limit, setLimit] = useState(null);
  const [loading, setLoading] = useState(true);
  const monthsToShow = Math.max(1, Math.min(12, months || 4));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await LicenseService.getEmailUsage();
      if (res?.data?.success && res?.data?.data) {
        const payload = res.data.data;
        const full = Array.isArray(payload.monthly_usage) ? payload.monthly_usage : [];
        setData(full.slice(-monthsToShow));
        setLimit(payload.current_month?.limit ?? null);
      }
    } catch (err) {
      console.error('Email usage monthly chart:', err);
    } finally {
      setLoading(false);
    }
  }, [monthsToShow]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const labels = useMemo(() =>
    data.map((item) => {
      const [y, m] = (item.year_month || '').split('-');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${monthNames[parseInt(m, 10) - 1] || ''} ${y || ''}`;
    }),
    [data]
  );

  const counts = useMemo(() => data.map((item) => item.count || 0), [data]);
  const isBar = chartType === 'bar';
  const hasLimit = limit != null && limit > 0;
  const barValues = counts;
  const greyMax = hasLimit ? limit : 100;
  const showGreyBar = isBar && hasLimit;
  const yAxisMax = showGreyBar ? Math.ceil((limit * 1.1) / 200) * 200 : undefined;
  const yAxisInterval = 200;

  const tooltipFormatter = useCallback((params) => {
    if (!Array.isArray(params) || params.length === 0) return '';
    const p = params.find((x) => x.seriesName === 'Emails sent') || params[0];
    const idx = p.dataIndex;
    const item = data[idx];
    if (!item) return '';
    const count = item.count || 0;
    const pct = hasLimit && limit > 0 ? Math.round((count / limit) * 100) : null;
    return `
      <div style="padding: 8px;">
        <div style="font-weight: 600; margin-bottom: 4px;">${p.name}</div>
        <div style="margin: 4px 0;">
          <span style="display: inline-block; width: 10px; height: 10px; background: ${getColor('primary')}; margin-right: 6px;"></span>
          Emails sent: ${numberFormatter(count, 0)}${pct != null ? ` (${pct}%)` : ''}
        </div>
      </div>
    `;
  }, [data, hasLimit, limit]);

  const option = useMemo(() => ({
    tooltip: {
      trigger: 'axis',
      formatter: tooltipFormatter,
      backgroundColor: rgbaColor(getColor('gray-800'), 0.95),
      borderColor: getColor('gray-600'),
      textStyle: { color: getColor('gray-100') },
    },
    xAxis: {
      type: 'category',
      data: labels,
      boundaryGap: isBar,
      axisPointer: { type: 'shadow' },
      axisLabel: {
        color: getColor('gray-400'),
        fontSize: isBar && labels.length > 6 ? 10 : 11,
        rotate: isBar && labels.length > 6 ? 45 : 0,
      },
      axisTick: { show: false },
      axisLine: { show: false },
    },
    yAxis: {
      type: 'value',
      position: 'left',
      min: 0,
      max: yAxisMax,
      interval: isBar ? yAxisInterval : undefined,
      axisPointer: { show: false },
      splitLine: {
        lineStyle: {
          color: getColor('gray-300'),
          type: 'dashed',
        },
      },
      axisLabel: {
        show: true,
        color: getColor('gray-400'),
        fontSize: 10,
        formatter: (value) => numberFormatter(value, 0),
        margin: 12,
        align: 'right',
      },
      axisTick: { show: false },
      axisLine: { show: false },
    },
    series: isBar
      ? [
          ...(showGreyBar
            ? [{
                name: 'Max',
                type: 'bar',
                data: labels.map(() => greyMax),
                itemStyle: { color: getColor('gray-100') },
                barGap: '-100%',
                barMaxWidth: 40,
              }]
            : []),
          {
            name: 'Emails sent',
            type: 'bar',
            data: barValues,
            itemStyle: { color: getColor('primary') },
            barGap: showGreyBar ? '-100%' : undefined,
            barMaxWidth: 40,
          },
        ]
      : [
          {
            name: 'Emails sent',
            type: 'line',
            data: counts,
            lineStyle: { color: getColor('primary') },
            itemStyle: {
              borderColor: getColor('primary'),
              borderWidth: 1,
            },
            symbol: 'circle',
            symbolSize: 6,
            smooth: true,
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: rgbaColor(getColor('primary'), 0.2) },
                  { offset: 1, color: rgbaColor(getColor('primary'), 0) },
                ],
              },
            },
          },
        ],
    grid: { right: 8, left: 48, bottom: isBar && labels.length > 6 ? 40 : 24, top: 8, containLabel: true },
  }), [labels, counts, barValues, tooltipFormatter, isBar, showGreyBar, greyMax, yAxisMax, yAxisInterval]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 140 }}>
        <Spin indicator={<LoadingOutlined spin />} size="small" />
      </div>
    );
  }

  const chartHeight = isBar && labels.length > 6 ? 220 : 140;

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={option}
      style={{ height: chartHeight }}
    />
  );
};

export default EmailUsageMonthlyChart;
